import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, type SocialActivityList } from '../api';
import { num, t } from '../i18n';
import { go } from '../router';
import { subscribeLive } from '../live';

@customElement('activity-indicator')
export class ActivityIndicator extends LitElement {
  @state() private list?: SocialActivityList;
  private unsubscribe?: () => void;
  private marking = false;

  protected createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    void this.load().then(() => { if (this.isConnected) this.unsubscribe = subscribeLive(() => this.load()); });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = undefined;
  }

  protected updated() {
    if (this.marking || !this.list || document.visibilityState === 'hidden') return;
    const button = this.querySelector<HTMLButtonElement>('.activity-nav');
    if (!button) return;
    const rect = button.getBoundingClientRect();
    if (rect.bottom <= 0 || rect.top >= innerHeight || rect.right <= 0 || rect.left >= innerWidth) return;
    const ids = this.list.items.filter((item) => item.state === 'available' && !item.indicatorShownAt).map((item) => item.id);
    if (!ids.length) return;
    this.marking = true;
    void api.activityIndicator(ids).then(() => {
      const markedAt = new Date().toISOString();
      const shown = new Set(ids);
      // Assigning the @state() field schedules the render; the explicit call
      // below covers the case where the read raced (list replaced meanwhile).
      if (this.list) this.list = { ...this.list, items: this.list.items.map((item) => shown.has(item.id) ? { ...item, indicatorShownAt: markedAt } : item) };
      this.requestUpdate();
    }).catch(() => {
      // No render of our own: the ids are still unmarked, so a render here
      // would re-run updated() and POST again at once. The next live update
      // (subscribeLive -> load() -> render -> updated()) retries the mark.
    }).finally(() => {
      this.marking = false;
    });
  }

  private async load() {
    try { this.list = await api.activity(); } catch { /* social activity must not interrupt the home screen */ }
  }

  render() {
    const unread = this.list?.unread ?? 0;
    const actionable = this.list?.actionable ?? 0;
    return html`<button type="button" class="home-pill activity-nav" aria-label=${actionable
      ? t('activity.navCount', { actionable, unread }) : t('activity.nav')} @click=${() => go('/activity')}>
      ${t('activity.nav')}${actionable ? html`<span class="activity-nav-count" aria-hidden="true">${num(actionable)}</span>` : nothing}
      <style>
        activity-indicator{display:inline-flex}.activity-nav{display:inline-flex;align-items:center;gap:6px;white-space:nowrap}
        .activity-nav-count{display:inline-grid;place-items:center;min-width:18px;height:18px;padding:0 3px;box-sizing:border-box;border-radius:9px;background:var(--accent,#7252a5);color:#fff;font-size:11px;font-weight:700;line-height:1}
      </style>
    </button>`;
  }
}

declare global { interface HTMLElementTagNameMap { 'activity-indicator': ActivityIndicator } }
